/**
 * Renaming or moving your own shared folder, or a folder above one, in My Files (E10, P1-A14).
 *
 * The Files browser routes such a rename or move here instead of to the storage API: a dry run says
 * which shared folders it carries and whether the destination is free; the user confirms; then the
 * whole selection goes to `POST /v1/shares/moves` and is followed in a toast until it ends.
 */
import React, {createContext, useContext, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useQuery} from '@tanstack/react-query'
import {Id, toast} from 'react-toastify'
import {toastActionableConfig, toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {SHARED_FOLDER_ICON} from './constants'
import {ShareOperationTimeoutError} from './shareOperations'
import {
  dryRunOwnerMove,
  OwnerMoveBody,
  OwnerMoveOperation,
  ownerMoveErrorMessage,
  ownerMoveWarning,
  ShareMove,
  startOwnerMove,
  waitForOwnerMove,
} from './shareMoves'
import './sharing.scss'

/** An item of My Files: a file, or a folder ending in `/`. */
export interface ShareMoveItem {
  path: string
  name: string
  isDir: boolean
}

/** What the user asked for: items moved into a folder, or one item renamed. */
export type ShareMoveIntent =
  | {
      kind: 'move'
      items: ShareMoveItem[]
      /** The destination folder: `''` for the top of My Files, else `a/b/`. */
      destination: string
    }
  | {kind: 'rename'; item: ShareMoveItem; newName: string}

export interface ShareMoveOutcome {
  intent: ShareMoveIntent
  status: 'succeeded' | 'failed'
  /** The shared folders the move carried (or would have). */
  shares: ShareMove[]
}

/** The request body for an intent. The whole selection goes in one request. */
export function shareMoveBody(intent: ShareMoveIntent): OwnerMoveBody {
  return intent.kind === 'rename'
    ? {path: intent.item.path, newName: intent.newName}
    : {sources: intent.items.map((item) => item.path), destination: intent.destination}
}

const quoted = (name: string) => `“${name}”`

/** `“a”`, `“a” and “b”`, `“a”, “b” and “c”`. */
function namesList(names: string[]): string {
  const q = names.map(quoted)
  return q.length <= 1 ? q.join('') : `${q.slice(0, -1).join(', ')} and ${q[q.length - 1]}`
}

/** The item(s) for messages: `“Data”` or `3 items`. */
function whatLabel(intent: ShareMoveIntent): string {
  if (intent.kind === 'rename') return quoted(intent.item.name)
  return intent.items.length === 1
    ? quoted(intent.items[0].name)
    : `${intent.items.length.toLocaleString()} items`
}

/** `a/b/` as `My Files › a › b`. */
const placeLabel = (folder: string) =>
  ['My Files', ...folder.split('/').filter(Boolean)].join(' › ')

const lastSegment = (path: string) => path.replace(/\/$/, '').split('/').pop() ?? path

/** What the confirmation says about the shared folders the move carries. */
export function shareMoveExplanation(intent: ShareMoveIntent, shares: ShareMove[]): string[] {
  if (shares.length === 0) {
    // The list of shares on screen was out of date: nothing shared is involved after all.
    return ['None of these items is shared any more.']
  }
  const moved = new Set(
    intent.kind === 'rename' ? [intent.item.path] : intent.items.map((item) => item.path)
  )
  const names = namesList(shares.map((share) => share.name || lastSegment(share.from)))
  const one = shares.length === 1
  const allSelected = shares.every((share) => moved.has(share.from))
  return [
    allSelected
      ? `${names} ${one ? 'is' : 'are'} shared.`
      : `This includes the shared ${one ? 'folder' : 'folders'} ${names}.`,
    one
      ? 'Its members keep access and will find it at the new location. While the files are copied it is read-only for them.'
      : 'Their members keep access and will find them at the new location. While the files are copied they are read-only for them.',
    'Links to the old location (in .box projects or job records) will stop working.',
  ]
}

const ShareMoveContext = createContext<((intent: ShareMoveIntent) => void) | null>(null)

/** Opens the confirmation for a rename or move carrying a shared folder; null outside a provider. */
export function useOpenShareMove(): ((intent: ShareMoveIntent) => void) | null {
  return useContext(ShareMoveContext)
}

/**
 * Holds the confirmation for a Files browser. `onFinished` runs when a move ends (it may be long
 * after the dialog closed), so the browser can refresh the folders and the shares.
 */
export const ShareMoveProvider: React.FC<{
  children: React.ReactNode
  onFinished?: (outcome: ShareMoveOutcome) => void
}> = ({children, onFinished}) => {
  const [intent, setIntent] = useState<ShareMoveIntent | null>(null)
  const onFinishedRef = useRef(onFinished)
  onFinishedRef.current = onFinished
  return (
    <ShareMoveContext.Provider value={setIntent}>
      {children}
      {intent && (
        <ShareMoveDialog
          intent={intent}
          onHide={() => setIntent(null)}
          onFinished={(outcome) => onFinishedRef.current?.(outcome)}
        />
      )}
    </ShareMoveContext.Provider>
  )
}

export interface ShareMoveDialogProps {
  intent: ShareMoveIntent
  onHide: () => void
  onFinished?: (outcome: ShareMoveOutcome) => void
}

const MAX_CONFLICTS_LISTED = 10

/**
 * The confirmation: what the move carries along and what that means for the members. When the
 * destination is taken, or the dry run refuses the move, it says why instead and offers no Move.
 */
export const ShareMoveDialog: React.FC<ShareMoveDialogProps> = ({intent, onHide, onFinished}) => {
  const body = useMemo(() => shareMoveBody(intent), [intent])
  const plan = useQuery({
    queryKey: ['sharing', 'move-plan', body],
    queryFn: () => dryRunOwnerMove(body),
    retry: false,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  })
  const rename = intent.kind === 'rename'
  const shares = plan.data?.shares ?? []
  const conflicts = plan.data?.conflicts ?? []
  const ready = plan.isSuccess && conflicts.length === 0
  const several = shares.length > 1
  const title = `${rename ? 'Rename' : 'Move'} shared ${several ? 'folders' : 'folder'}?`

  const run = () => {
    if (!ready) return
    onHide()
    void runShareMove({intent, shares, onFinished})
  }

  return (
    <Modal
      show
      onHide={onHide}
      centered
      aria-labelledby='share-move-title'
      dialogClassName='ob-sharing-dialog'
      data-testid='share-move-dialog'
    >
      <Modal.Header closeButton>
        <Modal.Title id='share-move-title' className='text-truncate'>
          <i className={`${SHARED_FOLDER_ICON} me-2`} aria-hidden='true' />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <dl className='ob-sharing-transfer__route mb-3'>
          <dt>{rename ? 'Rename' : 'Move'}</dt>
          <dd data-testid='share-move-what'>{whatLabel(intent)}</dd>
          <dt>{rename ? 'To' : 'Into'}</dt>
          <dd data-testid='share-move-to'>
            {intent.kind === 'rename' ? quoted(intent.newName) : placeLabel(intent.destination)}
          </dd>
        </dl>

        <div aria-live='polite' data-testid='share-move-plan'>
          {plan.isLoading ? (
            <span className='text-muted'>
              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
              Checking…
            </span>
          ) : plan.isError ? (
            <div className='alert alert-danger small mb-0' role='alert'>
              {ownerMoveErrorMessage(plan.error)}
            </div>
          ) : conflicts.length > 0 ? (
            <div className='alert alert-warning small mb-0' data-testid='share-move-conflicts'>
              Something with that name already exists there:
              <ul className='mb-0 mt-1'>
                {conflicts.slice(0, MAX_CONFLICTS_LISTED).map((path) => (
                  <li key={path} className='text-break'>
                    {path}
                  </li>
                ))}
                {conflicts.length > MAX_CONFLICTS_LISTED && (
                  <li>and {conflicts.length - MAX_CONFLICTS_LISTED} more</li>
                )}
              </ul>
              Rename or move it first.
            </div>
          ) : (
            shareMoveExplanation(intent, shares).map((line, index) => (
              <p key={index} className={index === 0 ? 'fw-semibold mb-2' : 'mb-2'}>
                {line}
              </p>
            ))
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onHide}>
          {ready || plan.isLoading ? 'Cancel' : 'Close'}
        </button>
        {(ready || plan.isLoading) && (
          <button
            type='button'
            className='btn btn-primary'
            onClick={run}
            disabled={!ready}
            data-testid='share-move-run'
          >
            {rename ? 'Rename' : 'Move'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

const VERBS = {
  move: {ing: 'Moving', ed: 'Moved', base: 'move'},
  rename: {ing: 'Renaming', ed: 'Renamed', base: 'rename'},
} as const

/** The toast's text while the move runs: `Moving “Data”… 120 of 400 files`. */
export function shareMoveProgress(
  intent: ShareMoveIntent,
  operation: Pick<OwnerMoveOperation, 'phase' | 'done' | 'objects'>
): string {
  const verb = VERBS[intent.kind].ing
  const what = whatLabel(intent)
  const total = operation.objects ?? 0
  if (operation.phase === 'copy' && total > 0) {
    const done = Math.min(operation.done ?? 0, total)
    return `${verb} ${what}… ${done.toLocaleString()} of ${total.toLocaleString()} ${
      total === 1 ? 'file' : 'files'
    }`
  }
  if (operation.phase === 'switch' || operation.phase === 'delete' || operation.phase === 'sweep') {
    return `${verb} ${what}… finishing`
  }
  return `${verb} ${what}…`
}

/** Starts the move, follows it in a toast, then reports to `onFinished`. */
export async function runShareMove({
  intent,
  shares,
  onFinished,
}: {
  intent: ShareMoveIntent
  shares: ShareMove[]
  onFinished?: (outcome: ShareMoveOutcome) => void
}): Promise<void> {
  const verb = VERBS[intent.kind]
  const what = whatLabel(intent)
  const toastId: Id = toast.loading(`${verb.ing} ${what}…`, {
    ...toastDefaultConfig,
    autoClose: false,
    closeOnClick: false,
  })
  let status: ShareMoveOutcome['status'] = 'failed'
  let carried = shares
  try {
    const started = await startOwnerMove(shareMoveBody(intent))
    carried = started.shares.length > 0 ? started.shares : shares
    const operation = await waitForOwnerMove(started.operationId, {
      onProgress: (op) => toast.update(toastId, {render: shareMoveProgress(intent, op)}),
    })
    status = 'succeeded'
    const warning = ownerMoveWarning(operation)
    toast.update(
      toastId,
      warning
        ? {render: warning, type: 'warning', isLoading: false, ...toastActionableConfig}
        : {render: `${verb.ed} ${what}.`, type: 'success', isLoading: false, ...toastDefaultConfig}
    )
  } catch (error) {
    getLogger().warn('Moving a shared folder failed', error)
    const stillRunning = error instanceof ShareOperationTimeoutError
    toast.update(toastId, {
      render: (
        <div data-testid='share-move-failed'>
          {!stillRunning && (
            <div className='fw-bold'>
              Couldn't {verb.base} {what}.
            </div>
          )}
          <div>{ownerMoveErrorMessage(error)}</div>
        </div>
      ),
      type: stillRunning ? 'info' : 'error',
      isLoading: false,
      ...toastActionableConfig,
    })
  } finally {
    onFinished?.({intent, status, shares: carried})
  }
}
